import { message } from "antd";
import axios, { AxiosError, AxiosResponse } from "axios";
import AppConfig from "../config";

const apiClient = axios.create({
  baseURL: AppConfig.API_URL,
});

apiClient.interceptors.request.use(
  (config: any) => {
    const token = localStorage.getItem("ACCOUNTING_TOKEN");

    if (token) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      };
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error: AxiosError) => {
    const response = error.response;

    if (response && response.status === 401) {
      localStorage.removeItem("ACCOUNTING_USER");
      window.location.href = "/login";
    }

    return Promise.reject(error);
  }
);

interface RequestConfig {
  responseType?: "blob" | "json";
  fileName?: string;
  headers?: any;
  sendWholeResponse?: boolean;
}

// Function to make API requests
const apiRequest = async (
  url: string,
  method: "GET" | "POST" | "PUT" | "DELETE",
  data?: any,
  config: RequestConfig = {}
) => {
  try {
    const {
      responseType = "json",
      fileName,
      headers,
      sendWholeResponse = false,
    } = config;

    const response = await apiClient.request({
      url,
      method,
      data: method !== "GET" ? data : undefined,
      params: method === "GET" ? data?.params ?? undefined : undefined,
      headers,
      responseType,
    });

    if (responseType === "blob") {
      const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/octet-stream",
      });

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = fileName || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      return { success: true };
    }

    return sendWholeResponse ? response : response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      const errorMessage = error.response?.data?.message || error.message;
      message.error(errorMessage);
    }
    throw error;
  }
};

export default apiRequest;
